import { createContext, useContext, useEffect, useState } from "react";
import {
  Home as HomeIcon,
  Truck,
  Sparkles,
  Building2,
  Repeat,
  Trash2,
  Brush,
  Trees,
  ClipboardList,
  Briefcase,
} from "lucide-react";
import api from "@/lib/api";

const ServicesContext = createContext({ services: [], loading: true });

/** Stable slug derived from service name. Used for routing/testids only. */
export function slugifyServiceName(name) {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Resolve a Lucide icon for a service based on its name + category.
 * Falls back to Sparkles. Kept here (not in DB) until an icon field is added.
 */
export function iconForService(service) {
  const name = String(service?.name || "").toLowerCase();
  const cat = String(service?.category || "").toLowerCase();
  if (/(move-?in|move in)/.test(name)) return HomeIcon;
  if (/(move-?out|move out)/.test(name)) return Truck;
  if (/(pre-?listing|listing)/.test(name)) return Sparkles;
  if (/(property|turnover|landlord)/.test(name)) return ClipboardList;
  if (/(biweekly|monthly|recurring)/.test(name)) return Repeat;
  if (/(office|commercial|business)/.test(name)) return Building2;
  if (/(junk|haul)/.test(name)) return Trash2;
  if (/(carpet)/.test(name)) return Brush;
  if (/(yard|mow|edg)/.test(name)) return Trees;
  if (cat === "commercial") return Building2;
  if (cat === "residential") return HomeIcon;
  if (cat === "extras") return Briefcase;
  return Sparkles;
}

export function ServicesProvider({ children }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/services")
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setServices(list.map((s) => ({ ...s, slug: slugifyServiceName(s.name) })));
      })
      .catch(() => {
        if (!cancelled) setServices([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <ServicesContext.Provider value={{ services, loading }}>
      {children}
    </ServicesContext.Provider>
  );
}

export function usePublicServices() {
  return useContext(ServicesContext);
}
